import React, { useEffect, useState } from 'react';
import {
    getplayerTable,
    getplayerTools,
    getplayerWorkers,
    fetchDecayData
} from '../api/waxApi';
import NFT_TEMPLATES from '../config/nftTemplates';
import WORKER_TEMPLATES from '../config/workerTemplates';
import './WalletManager.css';

import ranchIcon from '../assets/icons/ranch.webp';
import farmIcon from '../assets/icons/farm.webp';
import toolIcon from '../assets/icons/tool.webp';
import rcoinIcon from '../assets/icons/rcoin.webp';
import waxpIcon from '../assets/icons/waxp.webp';

const TOKEN_ICONS = {
    RANCH: ranchIcon,
    FARM: farmIcon,
    TOOL: toolIcon,
    RCOIN: rcoinIcon
};

const formatTime = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${h.toString().padStart(2, '0')}:${m
        .toString()
        .padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

function isValidEosioName(name) {
    return /^[a-z1-5.]{1,12}$/.test(name);
}

export default function WalletManager({ session, tokenData }) {
    const [wallet, setWallet] = useState('');
    const [wallets, setWallets] = useState([]);
    const [active, setActive] = useState('');
    const [hidden, setHidden] = useState(false);
    const [error, setError] = useState('');
    const [stats, setStats] = useState(null);
    const [now, setNow] = useState(Math.floor(Date.now() / 1000));
    const [waxpUsd, setWaxpUsd] = useState(null);

    useEffect(() => {
        fetch('https://api.binance.com/api/v3/ticker/price?symbol=WAXPUSDT')
            .then(res => res.json())
            .then(data => setWaxpUsd(parseFloat(data.price)))
            .catch(err => console.warn('Failed to fetch WAXP/USD:', err));
    }, []);

    /* -------------------------------------------------- */
    /* Helper – claim accumulation based on workers       */
    /* -------------------------------------------------- */
    const getNftClaimStatus = (nft, workerList) => {
        const tpl = NFT_TEMPLATES.find((t) => t.template_id === +nft.template_id);
        if (!tpl) return { current: 0, total: 1, remaining: 0 };

        // Cool‑down after season/session multiplier
        const cooldown = ((tpl.charged_time || 86400) * session.cooldown_multiplier) / 100;

        // Workers that match NFT type
        const matchedWorkers = workerList.filter((w) => {
            const wt = WORKER_TEMPLATES.find((t) => t.template_id === +w.template_id);
            return wt?.type === tpl.type;
        });

        // Extra stored claims provided by workers (rarity dependent)
        const extraClaims = matchedWorkers.reduce((sum, w) => {
            const wt = WORKER_TEMPLATES.find((t) => t.template_id === +w.template_id);
            return sum + (wt?.saved_claims || 0);
        }, 0);

        const totalClaimable = 1 + extraClaims;
        const next = nft.next_claim || 0;
        let current = 0;
        const nowSec = Math.floor(Date.now() / 1000);

        if (nowSec >= next) {
            const diff = nowSec - next;
            const fullCharges = Math.floor(diff / cooldown);
            current = Math.min(1 + fullCharges, totalClaimable);
        }

        const remaining = current >= totalClaimable ? 0 : next + current * cooldown - nowSec;
        return { current, total: totalClaimable, remaining };
    };

    /* -------------------------------------------------- */
    /* Effect – initialise wallet list                    */
    /* -------------------------------------------------- */
    useEffect(() => {
        const stored = JSON.parse(localStorage.getItem('wallet_list') || '[]');
        const current = localStorage.getItem('wallet_current') || stored[0] || '';
        setWallets(stored);
        if (current) handleSelectChange(current, stored);
    }, []);

    /* -------------------------------------------------- */
    /* Effect – refresh when session/token data ready     */
    /* -------------------------------------------------- */
    useEffect(() => {
        if (active && session && tokenData) fetchStats(active);
    }, [session, tokenData, active]);

    /* -------------------------------------------------- */
    /* Effect – update clock                              */
    /* -------------------------------------------------- */
    useEffect(() => {
        const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
        return () => clearInterval(id);
    }, []);

    /* -------------------------------------------------- */
    /* Storage helpers                                    */
    /* -------------------------------------------------- */
    const updateStorage = (list, current) => {
        localStorage.setItem('wallet_list', JSON.stringify(list));
        localStorage.setItem('wallet_current', current || '');
    };

    /* -------------------------------------------------- */
    /* Daily profit helpers                               */
    /* -------------------------------------------------- */
    const getNftDaily = (nft, workerList) => {
        const tpl = NFT_TEMPLATES.find(t => +t.template_id === +nft.template_id);
        if (!tpl || !session || !tokenData) return 0;

        const [rewardAmt, rewardSym] = tpl.rewards[0].split(' ');
        const rewardUnits = +rewardAmt;
        const staminaUnits = tpl.consumed?.first || 0;
        const duraUnits = tpl.consumed?.second || 0;

        const rewardPrice = +tokenData[rewardSym]?.price || 0;
        const staminaPrice = +tokenData.FARM?.price || 0;
        const duraPrice = +tokenData.TOOL?.price || 0;

        const unitsPerClaimReward = (rewardUnits * session.reward_multiplier) / 100;
        const unitsPerClaimStam = (staminaUnits * session.stamina_cost) / 100;
        const unitsPerClaimDura = (duraUnits * session.durability_cost) / 100;

        const profitPerClaim =
            unitsPerClaimReward * rewardPrice -
            unitsPerClaimStam * staminaPrice -
            unitsPerClaimDura * duraPrice;

        const cooldown = ((tpl.charged_time || 86400) * session.cooldown_multiplier) / 100;
        const claimsDay = 86400 / cooldown;

        const matched = workerList.filter(
            w => (WORKER_TEMPLATES.find(t => +t.template_id === +w.template_id)?.type) === tpl.type
        );
        const luckyPct = matched.reduce((p, w) => {
            const wt = WORKER_TEMPLATES.find(t => +t.template_id === +w.template_id);
            return p + (wt?.lucky || 0);
        }, 0);

        const bonusUnits = unitsPerClaimReward * claimsDay * (luckyPct / 100);
        const bonusWax = bonusUnits * rewardPrice;

        return profitPerClaim * claimsDay + bonusWax;
    };

    const getWorkerDaily = (worker, dailyTokens) => {
        const tpl = WORKER_TEMPLATES.find(t => +t.template_id === +worker.template_id);
        if (!tpl || !session || !tokenData) return 0;

        const rewardUnits = tpl.reward || 0;          // RCOIN
        const staminaUnits = 100;

        const rewardPrice = +tokenData.RCOIN?.price || 0;
        const staminaPrice = +tokenData.FARM?.price || 0;

        const unitsPerClaimReward = (rewardUnits * session.reward_multiplier) / 100;
        const unitsPerClaimStam = (staminaUnits * session.stamina_cost) / 100;

        const cooldown = ((tpl.charged_time || 86400) * session.cooldown_multiplier) / 100;
        const claimsDay = 86400 / cooldown;

        // token akışı
        dailyTokens.RCOIN += unitsPerClaimReward * claimsDay;
        dailyTokens.FARM -= unitsPerClaimStam * claimsDay;

        const profitPerClaim = unitsPerClaimReward * rewardPrice - unitsPerClaimStam * staminaPrice;
        return profitPerClaim * claimsDay;                // WAX değeri
    };

    /* -------------------------------------------------- */
    /* Fetch player stats                                 */
    /* -------------------------------------------------- */
    const fetchStats = async (account) => {
        if (!account) return setStats(null);

        try {
            const [gameData, accountInfo, waxBalance] = await Promise.all([
                getplayerTable(account),
                fetch('https://api.wax.alohaeos.com/v1/chain/get_account', {
                    method: 'POST',
                    body: JSON.stringify({ account_name: account })
                }).then((r) => r.json()),
                fetch('https://api.wax.alohaeos.com/v1/chain/get_currency_balance', {
                    method: 'POST',
                    body: JSON.stringify({ code: 'eosio.token', account, symbol: 'WAX' })
                }).then((r) => r.json())
            ]);

            const cpuPercent =
                accountInfo.cpu_limit?.used && accountInfo.cpu_limit?.max
                    ? ((accountInfo.cpu_limit.used / accountInfo.cpu_limit.max) * 100).toFixed(1)
                    : 0;

            setStats({
                ...gameData,
                wax_balance: waxBalance[0] || '0.0000',
                staking: accountInfo.total_resources?.cpu_weight || '0.0000',
                net: accountInfo.total_resources?.net_weight || '0.0000',
                cpu_percent: cpuPercent,
                daily_tokens: { RANCH: 0, FARM: 0, TOOL: 0, RCOIN: 0 }
            });
        } catch {
            setStats(null);
            return;
        }

        const data = await getplayerTable(account);
        const balances = Array.isArray(data.balances) ? data.balances : Object.values(data.balances);
        const stamina = { ...data.stamina };
        const assist = data.assistant_account || '';

        const nftList = await Promise.all(
            data.staked_nft.map(async (id) => {
                const n = await getplayerTools(id);
                return n
                    ? {
                        asset_id: n.asset_id,
                        owner: n.owner,
                        template_id: n.template_id,
                        type: n.type,
                        durability: n.durability,
                        next_claim: n.next_claim,
                        join_time: n.join_time
                    }
                    : null;
            })
        ).then((l) => l.filter(Boolean));

        const workerList = await Promise.all(
            data.staked_workers.map(async (id) => {
                const w = await getplayerWorkers(id);
                return w
                    ? {
                        asset_id: w.asset_id,
                        template_id: w.template_id,
                        type: w.type,
                        owner: w.owner,
                        next_claim: w.next_claim,
                        join_time: w.join_time
                    }
                    : null;
            })
        ).then((l) => l.filter(Boolean));

        const dailyTokens = { RANCH: 0, FARM: 0, TOOL: 0, RCOIN: 0 };
        const workerBonusMap = {};   // asset_id → {sym, units, wax}

        const nftDaily = nftList.reduce((sum, nft) => {
            const tpl = NFT_TEMPLATES.find(t => +t.template_id === +nft.template_id);
            if (!tpl || !session || !tokenData) return sum;

            const [rewardAmt, rewardSym] = tpl.rewards[0].split(' ');
            const rewardUnits = +rewardAmt;
            const rewardPrice = +tokenData[rewardSym]?.price || 0;

            const staminaUnits = tpl.consumed?.first || 0;
            const duraUnits = tpl.consumed?.second || 0;
            const staminaPrice = +tokenData.FARM?.price || 0;
            const duraPrice = +tokenData.TOOL?.price || 0;

            const unitsPerClaimReward = (rewardUnits * session.reward_multiplier) / 100;
            const unitsPerClaimStam = (staminaUnits * session.stamina_cost) / 100;
            const unitsPerClaimDura = (duraUnits * session.durability_cost) / 100;

            const cooldown = ((tpl.charged_time || 86400) * session.cooldown_multiplier) / 100;
            const claimsDay = 86400 / cooldown;

            // Lucky toplamı (%)
            const matched = workerList.filter(
                w => (WORKER_TEMPLATES.find(t => +t.template_id === +w.template_id)?.type) === tpl.type
            );
            const luckyTotalPct = matched.reduce((p, w) => {
                const wt = WORKER_TEMPLATES.find(t => +t.template_id === +w.template_id);
                return p + (wt?.lucky || 0);
            }, 0);

            const bonusMultiplier = luckyTotalPct / 100;           // ör. 20 % ⇒ 0.2
            const bonusUnits = unitsPerClaimReward * claimsDay * bonusMultiplier;
            const bonusWax = bonusUnits * rewardPrice;

            // bonusu ilgili worker’lara dağıt
            matched.forEach(w => {
                const wt = WORKER_TEMPLATES.find(t => +t.template_id === +w.template_id);
                const indivUnits = unitsPerClaimReward * claimsDay * ((wt?.lucky || 0) / 100);
                const indivWax = indivUnits * rewardPrice;
                if (!workerBonusMap[w.asset_id]) workerBonusMap[w.asset_id] = { sym: rewardSym, units: 0, wax: 0 };
                workerBonusMap[w.asset_id].units += indivUnits;
                workerBonusMap[w.asset_id].wax += indivWax;
            });

            // token akışı
            dailyTokens[rewardSym] += unitsPerClaimReward * claimsDay + bonusUnits;
            dailyTokens.FARM -= unitsPerClaimStam * claimsDay;
            dailyTokens.TOOL -= unitsPerClaimDura * claimsDay;

            const baseWax = (unitsPerClaimReward * rewardPrice -
                unitsPerClaimStam * staminaPrice -
                unitsPerClaimDura * duraPrice) * claimsDay;

            return sum + baseWax;
        }, 0);

        let workerDaily = 0;
        const workerListWithCalc = workerList.map(w => {
            const baseWax = getWorkerDaily(w, dailyTokens);        // RCOIN geliri (WAX cinsinden)
            const bonus = workerBonusMap[w.asset_id] || { sym: '', units: 0, wax: 0 };
            workerDaily += baseWax + bonus.wax;
            return {
                ...w,
                daily_wax: (baseWax),
                bonus_units: bonus.units,
                bonus_sym: bonus.sym
            };
        });

        setStats(prev => ({
            ...prev,
            balances,
            stamina,
            assistant_account: assist,
            nftList,
            workerList: workerListWithCalc,
            daily_profit: (nftDaily + workerDaily).toFixed(2),
            daily_tokens: dailyTokens
        }));
    };

    /* -------------------------------------------------- */
    /* Wallet actions                                     */
    /* -------------------------------------------------- */
    const handleSelectChange = (name, list = wallets) => {
        setActive(name);
        updateStorage(list, name);
        fetchStats(name);
    };

    const handleAdd = async () => {
        const val = wallet.trim();
        if (!isValidEosioName(val)) return;
        if (!(await getplayerTable(val))) return setError('This wallet is not registered in the game.');
        const newList = [...new Set([val, ...wallets])];
        setWallets(newList);
        handleSelectChange(val, newList);
        setWallet('');
        setError('');
    };

    const handleRemove = (target) => {
        const filtered = wallets.filter((w) => w !== target);
        const newActive = target === active ? '' : active;
        setWallets(filtered);
        handleSelectChange(newActive, filtered);
    };

    /* -------------------------------------------------- */
    /* Render                                             */
    /* -------------------------------------------------- */
    return (
        <div className="wallet-wrapper">
            <div className="wallet-panel">
                <input
                    type="text"
                    value={wallet}
                    maxLength={12}
                    onChange={(e) => {
                        setWallet(e.target.value.toLowerCase().replace(/[^a-z1-5.]/g, ''));
                        setError('');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                    placeholder="e.g. abcde.wam"
                    className="wallet-input"
                />
                {error && <div className="wallet-error">{error}</div>}

                <div className="wallet-menu">
                    <button onClick={handleAdd} disabled={!wallet}>
                        ADD
                    </button>
                    <button onClick={() => handleSelectChange('')} disabled={!active}>
                        CLEAR CURRENT
                    </button>
                    <button onClick={() => setHidden(!hidden)} disabled={!wallets.length}>
                        {hidden ? 'SHOW LIST' : 'HIDDEN LIST'}
                    </button>
                    <button
                        className="danger-btn"
                        onClick={() => {
                            setWallets([]);
                            handleSelectChange('', []);
                            setError('');
                        }}
                        disabled={!wallets.length}
                    >
                        CLEAR ALL
                    </button>
                </div>

                {!hidden && (
                    <div className="wallet-list">
                        {wallets.map((w) => (
                            <div key={w} className={`wallet-item ${w === active ? 'active' : ''}`}>
                                <span onClick={() => handleSelectChange(w)}>{w}</span>
                                <button className="remove-x" onClick={() => handleRemove(w)}>
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {active && stats && (
                <div className="stats-card">
                    <div className="header-row">
                        <span className="account">{active}</span>

                        <div className={`daily-badge ${+stats.daily_profit >= 0 ? 'pos' : 'neg'}`}>
                            <span className="daily-label">Daily:</span>
                            <span className="wax-amount">{stats.daily_profit} WAX</span>
                            {waxpUsd && (
                                <span className="usd-amount">(${(parseFloat(stats.daily_profit) * waxpUsd).toFixed(2)})</span>
                            )}
                            
                        </div>
                    </div>

                    <div className="stats-header">
                        <div className="balances">
                            <div>
                                <span className="label">
                                    <strong>Balance</strong>
                                </span>
                                <span className="value">{parseFloat(stats.wax_balance).toFixed(4)} WAX</span>
                            </div>
                            <div>
                                <span className="label">
                                    <strong>CPU Staked</strong>
                                </span>
                                <span className="value">{parseFloat(stats.staking).toFixed(4)} WAX</span>
                            </div>
                        </div>

                        <div className="section-label">CPU</div>
                        <div className="cpu-row">
                            <div className="cpu-bar">
                                <div className="cpu-bar-fill" style={{ width: `${stats.cpu_percent}%` }} />
                            </div>
                            <span className="cpu-label">{stats.cpu_percent}%</span>
                        </div>

                        <div className="token-grid">
                            {['RANCH', 'FARM', 'TOOL', 'RCOIN'].map((sym) => (
                                <div
                                    key={sym}
                                    className={`token-box ${stats.daily_tokens[sym] >= 0 ? 'pos' : 'neg'}`}
                                >
                                    <span className="token-amount">{stats.daily_tokens[sym].toFixed(2)}</span>
                                    <img src={TOKEN_ICONS[sym]} alt={sym} className="token-icon" />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="stats-details">
                        <div className="stats-section">
                            <div className="section-label">Balances</div>
                            <div className="token-grid">
                                {stats.balances?.map((b, i) => {
                                    const [amount, sym] = b.split(' ');
                                    const icon = TOKEN_ICONS[sym];
                                    return (
                                        <div key={i} className="token-box">
                                            <span className="token-amount">{amount}</span>
                                            {icon && <img src={icon} alt={sym} className="token-icon" />}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="section-label">Stamina</div>
                        <div className="stamina-row">
                            <div className="stamina-bar">
                                <div
                                    className="stamina-bar-fill"
                                    style={{ width: `${(stats.stamina.first / stats.stamina.second) * 100}%` }}
                                />
                            </div>
                            <span className="stamina-label">
                                {stats.stamina.first} / {stats.stamina.second}
                            </span>
                        </div>

                        <div className="section-label">Assistance</div>
                        <div className="assistant-line">{stats.assistant_account || '-'}</div>

                        {/* -------------------------------------------------- */}
                        {/* NFT Table                                           */}
                        {/* -------------------------------------------------- */}
                        <div className="nft-table">
                            <div className="nft-row header">
                                <div>Name</div>
                                <div>Durability</div>
                                <div>Cooldown</div>
                                <div>Claims</div>
                                <div>Daily</div>
                            </div>

                            {stats.nftList?.map((n, i) => {
                                const tpl = NFT_TEMPLATES.find((t) => t.template_id === +n.template_id);
                                const maxDur = tpl?.max_durability ?? 0;
                                const curDur = n.durability.first;
                                const durPct = maxDur ? (curDur / maxDur) * 100 : 0;

                                const { current: claimCur, total: claimTot, remaining } = getNftClaimStatus(n, stats.workerList || []);

                                return (
                                    <div key={i} className="nft-row">
                                        <div>{tpl?.nft_name || `NFT #${n.asset_id}`}</div>

                                        <div>
                                            <div className="dura-container">
                                                <div className="dura-fill" style={{ width: `${durPct}%` }} />
                                                <span className="dura-text">
                                                    {curDur} / {maxDur || '?'}
                                                </span>
                                            </div>
                                        </div>

                                        <div>
                                            <span className={`cd-badge ${remaining > 0 ? 'wait' : 'ready'}`}>{
                                                remaining > 0 ? formatTime(remaining) : 'READY'
                                            }</span>
                                        </div>

                                        <div>
                                            {claimCur}/{claimTot}
                                        </div>

                                        <div>
                                            {getNftDaily(n, stats.workerList).toFixed(2)}
                                            <img src={waxpIcon} alt="WAX" className="token-icon" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* -------------------------------------------------- */}
                        {/* Worker Table                                        */}
                        {/* -------------------------------------------------- */}
                        <div className="nft-table">
                            <div className="worker-row header">
                                <div>Name</div>
                                <div>Reward</div>
                                <div>Cooldown</div>
                                <div>Bonus</div>
                                <div>Daily</div>
                            </div>

                            {stats.workerList?.map((w, i) => {
                                const tpl = WORKER_TEMPLATES.find(t => +t.template_id === +w.template_id);
                                const remaining = w.next_claim ? w.next_claim - now : 0;
                                return (
                                    <div key={i} className="worker-row">
                                        <div>{tpl?.worker_name || `Worker #${w.asset_id}`} ({tpl?.type || w.type})</div>
                                        <div>{tpl?.reward || '?'}</div>
                                        <div>
                                            <span className={`cd-badge ${remaining > 0 ? 'wait' : 'ready'}`}>
                                                {remaining > 0 ? formatTime(remaining) : 'READY'}
                                            </span>
                                        </div>
                                        <div>
                                            {w.bonus_units.toFixed(2)}
                                            {w.bonus_sym && (
                                                <img src={TOKEN_ICONS[w.bonus_sym]} alt={w.bonus_sym} className="token-icon" />
                                            )}
                                        </div>
                                        <div>
                                            {w.daily_wax.toFixed(2)}
                                            <img src={waxpIcon} alt="WAX" className="token-icon" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
